import React, { useEffect, useRef, useState } from "react";
import { SERVER_URL } from "../../config/config";
import Loader from "../Loader";

function Donut({ value }) {
  const pctRaw = value != null ? Number(value) : 0;
  const pct = Math.min(Math.max(pctRaw, 0), 100);
  const displayPct = pct.toFixed(1);

  const strokeDasharray = `${pct} ${100 - pct}`;
  const isCritical = pct > 80;

  const color = pct > 80 ? "#ef4444" : pct > 60 ? "#f59e0b" : "#22c55e";

  return (
    <svg width="45" height="45" viewBox="0 0 36 36">
      <circle
        cx="18"
        cy="18"
        r="15.9155"
        fill="none"
        stroke="#e5e7eb"
        strokeWidth="3"
      />
      <circle
        cx="18"
        cy="18"
        r="15.9155"
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeDasharray={strokeDasharray}
        strokeDashoffset="25"
        strokeLinecap="round"
        style={isCritical ? { animation: "pulse-red 1.2s infinite" } : {}}
      />
      <text
        x="18"
        y="20"
        textAnchor="middle"
        fontSize="8"
        fill="#111827"
        fontWeight="600"
      >
        {displayPct}%
      </text>
    </svg>
  );
}

export default function ServerGrid({ searchTerm }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const HEALTH_CLASS_MAP = {
    OK: "health-green",
    WARNING: "health-orange",
  };

  const HEALTH_PRIORITY = {
    CRITICAL: 1,
    null: 1,
    WARNING: 2,
    OK: 3,
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      let allServers = [];
      let page = 1;
      let total = 0;

      do {
        const res = await fetch(
          SERVER_URL + "/tie_details/get_all_server_details",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              paginate: { number_of_rows: 100, page_number: page },
              filter_fields: {},
            }),
          },
        );

        const data = await res.json();

        if (data && data.server && Array.isArray(data.server[1])) {
          total = data.server[0];
          allServers = allServers.concat(data.server[1]);
        }

        page++;
      } while (allServers.length < total);

      setServers(allServers);
    } catch (err) {
      console.error("Server fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  const safeLower = (value) =>
    typeof value === "string" ? value.toLowerCase() : "";

  const display = (value) =>
    value !== null && value !== undefined ? value : "—";

  const filteredServers = servers
    .filter((server) => {
      const search = safeLower(searchTerm);
      return (
        safeLower(server.server_name).includes(search) ||
        safeLower(server.ip_address).includes(search) ||
        safeLower(server.provider).includes(search) ||
        safeLower(server.region).includes(search)
      );
    })
    .sort((a, b) => {
      const aPriority = HEALTH_PRIORITY[a.health_status] || 1;
      const bPriority = HEALTH_PRIORITY[b.health_status] || 1;
      return aPriority - bPriority;
    });

  if (loading) return <Loader text="Loading servers..." />;

  return (
    <div className="company-grid">
      {filteredServers.map((server) => {
        const healthClass =
          HEALTH_CLASS_MAP[server.health_status] || "health-red";

        return (
          <div className={"company-card "} key={server.server_id}>
            <div className="card-header">
              <h3>{server.server_name}</h3>
              <span className={"health-pill " + healthClass}>
                {display(server.health_status)}
              </span>
            </div>

            <div className="company-summary">
              <div>
                <span>Provider</span>
                {display(server.provider)}
              </div>
              <div>
                <span>IP Address</span>
                {display(server.ip_address)}
              </div>
              <div>
                <span>Region</span>
                {display(server.region)}
              </div>
              <div>
                <span>Type</span>
                {display(server.server_type)}
              </div>
              <div>
                <span>User</span>
                {display(server.user_name)}
              </div>
              <div>
                <span>CPU</span>
                {display(server.cpu)}
              </div>
              <div>
                <span>RAM (GB)</span>
                {display(server.ram_gb)}
              </div>
              <div>
                <span>Storage (GB)</span>
                {display(server.storage_gb)}
              </div>

              <div className="usage-box">
                <div className="usage-title">Resource Usage</div>

                <div className="donut-section">
                  <div className="donut-item">
                    <Donut value={server.cpu_usage} />
                    <span>CPU</span>
                  </div>
                  <div className="donut-item">
                    <Donut value={server.ram_usage} />
                    <span>RAM</span>
                  </div>
                  <div className="donut-item">
                    <Donut value={server.disk_usage} />
                    <span>Disk</span>
                  </div>
                </div>
              </div>

              {/* <div
                className={
                  "status " +
                  (server.status === "active"
                    ? "status-active"
                    : server.status === "inactive"
                    ? "status-inactive"
                    : "")
                }
              >
                <span>Status</span>
                <strong>
                  {server.status !== null && server.status !== undefined
                    ? server.status
                    : "—"}
                </strong>
              </div> */}
            </div>
          </div>
        );
      })}
    </div>
  );
}
